import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Pagination,
  styled,
  Dialog,
  DialogContent,
  Slide,
  DialogActions,
  TextField,
  Popover,
  IconButton,
  InputAdornment,
} from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import AddIcon from "@mui/icons-material/Add";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import CreateIcon from "@mui/icons-material/Create";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import jsPDF from "jspdf";
import "jspdf-autotable";
import PlayerEditForm from "./player_ranking_edit_form";
import ViewPlayer from "./player_ranking_view";
import useAuth from "../../hooks/useAuth";
import usePlayerData from "../../hooks/usePlayerData";
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});
const RankingTable = () => {
  const { isAuthenticated } = useAuth();
  const { data } = usePlayerData();
  const { deletePlayerData, addPlayerPoint } = usePlayerData();
  // PAGINATION
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const totalItems = data.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = data.slice(startIndex, endIndex);
  // FOR POINT ADDITION (FIX: Added active player state)
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPlayerForPoint, setSelectedPlayerForPoint] = useState(null);
  const [addPoint, setAddPoint] = useState("");
  const open = Boolean(anchorEl);
  const addPointId = open ? "popup-button" : undefined;
  // FOR EDITING
  const [editItem, setEditItem] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  // FOR DELETION
  const [deleteItemId, setDeleteItemId] = useState(null);
  // FOR VIEWING
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  // FOR CREATING PDF COLUMNS
  const columns = [
    { title: "Player Id", field: "id" },
    { title: "Rank", field: "player_rank" },
    { title: "Name", field: "name" },
    { title: "Game Events", field: "game_event" },
    // { title: "DOB", field: "dob" },
    { title: "Category", field: "category" },
    // { title: "Age", field: "age" },
    // { title: "Contact", field: "phone_number" },
    { title: "Club/District", field: "club" },
    { title: "Points", field: "point" },
  ];
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };
  const handleDownloadPDF = () => {
    const doc = new jsPDF("landscape"); // Landscape gives enough width for all columns
    // Title & Header
    doc.setFontSize(16);
    doc.text("NBA Player Ranking List", 14, 15);
    // Format dataset to safely include calculated & cleaned values
    const formattedBody = data.map((item) => ({
      id: item.id ?? "-",
      player_rank: item.player_rank ?? "-",
      name: item.name ?? "-",
      game_event: item.game_event ?? "-",
      formatted_dob: item.dob ? item.dob.split("T")[0] : "N/A",
      category: categorizePlayer(item.dob),
      club: item.club ?? "-",
      point: item.point ?? 0,
    }));
    doc.autoTable({
      margin: { top: 22 },
      theme: "striped",
      headStyles: {
        fillColor: [134, 30, 216], // Theme purple
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: "bold",
        halign: "center",
      },
      bodyStyles: {
        fontSize: 9,
        halign: "center",
      },
      columnStyles: {
        name: { halign: "left" }, // Align text left for player names
        club: { halign: "left" },
      },
      columns: columns.map((col) => ({ title: col.title, dataKey: col.field })),
      body: formattedBody,
    });
    doc.save("player_ranking_list.pdf");
  };
  // FIX: Open Popover and track target player
  const handleOpenAddPoint = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedPlayerForPoint(item);
  };
  // FIX: Reset popover and target player state on close
  const handleAddPointClose = () => {
    setAnchorEl(null);
    setSelectedPlayerForPoint(null);
    setAddPoint("");
  };
  const handlePlayerDeletion = (id) => {
    deletePlayerData(id);
    setDeleteItemId(null);
  };
  // FIX: Submit points using selectedPlayerForPoint state
  const handleAddPointSubmit = () => {
    if (!selectedPlayerForPoint || !addPoint) return;
    addPlayerPoint(
      selectedPlayerForPoint.id,
      addPoint,
      handleAddPointClose
    );
  };
  const StyledPagination = styled(Pagination)`
    && .MuiPaginationItem-root {
      color: #ffffff;
    }
  `;
  // Age calculation
  const parseDOB = (dobString) => {
    if (!dobString) return null;
    const cleanDateStr = dobString.split("T")[0];
    const [year, month, day] = cleanDateStr.split("-").map(Number);
    return { year, month: month - 1, day };
  };
  const calculateAge = (dobDate) => {
    if (!dobDate) return "N/A";
    const dob = parseDOB(dobDate);
    if (!dob) return "N/A";
    const today = new Date();
    let years = today.getFullYear() - dob.year;
    let months = today.getMonth() - dob.month;
    let days = today.getDate() - dob.day;
    if (days < 0) {
      months -= 1;
      const prevMonthLastDay = new Date(
        today.getFullYear(),
        today.getMonth(),
        0
      ).getDate();
      days += prevMonthLastDay;
    }
    if (months < 0) {
      years -= 1;
      months += 12;
    }
    return `${years}Y ${months}M ${days}D`;
  };
  const categorizePlayer = (dobDate) => {
    if (!dobDate) return "Uncategorized";
    const today = new Date();
    const birthDate = new Date(dobDate);
    let age = today.getFullYear() - birthDate.getUTCFullYear();
    const monthDiff = today.getMonth() - birthDate.getUTCMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getUTCDate())
    ) {
      age--;
    }
    if (age < 13) return "Under-13 (U13)";
    if (age >= 13 && age < 15) return "Under-15 (U15)";
    if (age >= 15 && age < 17) return "Under-17 (U17)";
    if (age >= 17 && age < 19) return "Under-19 (U19)";
    if (age >= 35 && age < 45) return "Senior Masters";
    if (age >= 45 && age < 55) return "Veteran Masters";
    if (age >= 55) return "Golden Masters";
    return "Open/General";
  };
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      gap="0"
      sx={{ overflow: "auto" }}
    >
      <Box
        sx={{
          width: "100%",
          display: "table",
          tableLayout: "fixed",
          // border: "1px solid yellow",
          // mt: 1,
        }}
      >
        <TableContainer sx={{ height: 480, overflowY: "auto", overflowX: "auto" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", color: "#00000090 !important", backgroundColor: "#fff" }}>
                  Rank
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "#00000090 !important", backgroundColor: "#fff" }}>
                  Id
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "#00000090 !important", backgroundColor: "#fff" }}>
                  Name
                </TableCell>
                {isAuthenticated && (
                  <TableCell sx={{ fontWeight: "bold", color: "#00000090 !important", backgroundColor: "#fff" }}>
                    Contact
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: "bold", color: "#00000090 !important", backgroundColor: "#fff" }}>
                  Game Category
                </TableCell>
                {isAuthenticated && (
                  <TableCell sx={{ fontWeight: "bold", color: "#00000090 !important", backgroundColor: "#fff" }}>
                    DOB
                  </TableCell>
                )}
                {isAuthenticated && (
                  <TableCell sx={{ fontWeight: "bold", color: "#00000090 !important", backgroundColor: "#fff" }}>
                    Age
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: "bold", color: "#00000090 !important", backgroundColor: "#fff" }}>
                  Category
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "#00000090 !important", backgroundColor: "#fff" }}>
                  Club/District
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "#00000090 !important", backgroundColor: "#fff" }}>
                  Points
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    color: "#00000090 !important",
                    textAlign: "center",
                  backgroundColor: "#fff",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentData.map((item, index) => (
                <TableRow key={item.id || index}>
                  <TableCell>{item.player_rank}</TableCell>
                  <TableCell>{item.u_id_generated}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  {isAuthenticated && <TableCell>{item.phone_number}</TableCell>}
                  <TableCell>{item.game_event}</TableCell>
                  {isAuthenticated && <TableCell>{item.dob.split("T")[0]}</TableCell>}
                  {isAuthenticated && (
                    <TableCell>{calculateAge(item.dob)}</TableCell>
                  )}
                  <TableCell>{categorizePlayer(item.dob)}</TableCell>
                  <TableCell>{item.club}</TableCell>
                  <TableCell>{item.point}</TableCell>
                  <TableCell>
                    <Box
                      display="flex"
                      gap="0.8rem"
                      alignItems="flex-end"
                      justifyContent="center"
                    >
                      {/* EDIT DIALOG */}
                      {isAuthenticated && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CreateIcon />}
                          style={{
                            color: "#861ED8",
                            borderColor: "#861ED8",
                          }}
                          onClick={() => {
                            setEditItem(item);
                            setOpenEditDialog(true);
                          }}
                        >
                          Edit
                        </Button>
                      )}
                      {/* DELETE BUTTON */}
                      {isAuthenticated && (
                        <span>
                          {deleteItemId === item.id ? (
                            <Box display="flex" gap="0.8rem">
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                style={{
                                  maxWidth: "40px",
                                  maxHeight: "30px",
                                  minWidth: "40px",
                                  minHeight: "30px",
                                }}
                                onClick={() => handlePlayerDeletion(item.id)}
                              >
                                <CheckIcon fontSize="small" />
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                style={{
                                  maxWidth: "40px",
                                  maxHeight: "30px",
                                  minWidth: "40px",
                                  minHeight: "30px",
                                }}
                                onClick={() => setDeleteItemId(null)}
                              >
                                <CloseIcon fontSize="small" />
                              </Button>
                            </Box>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DeleteForeverIcon />}
                              color="secondary"
                              style={{
                                color: "#FF0000",
                                borderColor: "#FF0000",
                              }}
                              onClick={() => setDeleteItemId(item.id)}
                            >
                              Delete
                            </Button>
                          )}
                        </span>
                      )}
                      {/* VIEW BUTTON */}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<RemoveRedEyeIcon />}
                        style={{
                          color: "#dc7cea",
                          borderColor: "#757ce8",
                        }}
                        onClick={() => {
                          setViewItem(item);
                          setOpenViewDialog(true);
                        }}
                      >
                        View
                      </Button>
                      {/* ADD POINT BUTTON */}
                      {isAuthenticated && (
                        <Button
                          size="small"
                          variant="outlined"
                          aria-describedby={addPointId}
                          startIcon={<AddIcon />}
                          style={{
                            color: "#cad902",
                            borderColor: "#cad902",
                          }}
                          onClick={(e) => handleOpenAddPoint(e, item)}
                        >
                          Add
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {/* SINGLE POPOVER (Moved Outside Table Loop) */}
        {isAuthenticated && (
          <Popover
            id={addPointId}
            open={open}
            anchorEl={anchorEl}
            onClose={handleAddPointClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            sx={{
              "& .MuiPopover-paper": {
                pt: 1,
                pb: 1,
                pl: 2,
                pr: 1,
              },
            }}
          >
            <TextField
              id="standard-number"
              type="text"
              label={`Add Point (${selectedPlayerForPoint?.name || ""})`}
              variant="standard"
              value={addPoint}
              required
              inputProps={{
                inputMode: "numeric",
                pattern: "-?[0-9]*",
              }}
              onChange={(e) => {
                const value = e.target.value;

                // Allows numbers, empty value, and minus sign
                if (/^-?\d*$/.test(value)) {
                  setAddPoint(value);
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      style={{ background: "#cad902" }}
                      onClick={handleAddPointSubmit}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Popover>
        )}
      </Box>
      <Box
        sx={{
          // border:"1px solid red",
          mt:1
        }}
      >
        <StyledPagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          size="large"
        />
      </Box>
      {/* download button */}
      <Box 
        sx={{
          // border:"1px solid white",
          mt:1
        }}
        alignSelf="flex-end"
      >
        <Button
          onClick={handleDownloadPDF}
          startIcon={<FileDownloadIcon />}
          variant="contained"
          style={{ backgroundColor: "#A91720", color: "white" }}
        >
          Download PDF
        </Button>
      </Box>
      {/* EDIT DIALOG */}
      <Dialog
        open={openEditDialog}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setOpenEditDialog(false)}
        aria-describedby="alert-dialog-slide-description"
        sx={{
          "& .MuiDialog-container": {
            justifyContent: "center",
            alignItems: "flex-start",
            "& .MuiPaper-root": {
              width: "100%",
              maxWidth: "350px",
            },
          },
        }}
      >
        <DialogContent>
          <PlayerEditForm
            editItem={editItem}
            setEditItem={setEditItem}
            setOpenEditDialog={setOpenEditDialog}
          />
        </DialogContent>
      </Dialog>
      {/* VIEW DIALOG */}
      <Dialog
        open={openViewDialog}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setOpenViewDialog(false)}
        aria-describedby="alert-dialog-slide-description"
        sx={{
          "& .MuiDialog-container": {
            justifyContent: "center",
            alignItems: "flex-start",
            "& .MuiPaper-root": {
              width: "100%",
              maxWidth: "500px",
            },
          },
        }}
      >
        <DialogContent
          sx={{
            paddingBottom: "0px",
            paddingLeft: "10px",
            paddingRight: "15px",
          }}
        >
          <ViewPlayer viewItem={viewItem} />
        </DialogContent>
        <DialogActions>
          <Button
            endIcon={<CloseIcon fontSize="large" />}
            sx={{ border: "1px solid blue" }}
            onClick={() => setOpenViewDialog(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default RankingTable;